import React, { useState, useEffect, useRef } from "react";
import { Input, Button, message } from "antd";
import "./ChangePasswordTab.css";
import axiosInstance from "../../Config/axiosConfig";

const ChangePasswordTab = ({username, deviceId, userIp, onSuccess}) => {
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const storedDeviceId = deviceId || localStorage.getItem("deviceId");
  const storedUserIp = userIp || localStorage.getItem("userIp");

  // Optional UX: resend OTP timer
  const [timer, setTimer] = useState(60);
  
  // Refs to track if OTP has been sent
  const hasSentOTP = useRef(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (step === 1 && timer > 0) {
      const t = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [timer, step]);

  // First, send OTP to user
  const handleSendOTP = async () => {
    // Prevent multiple simultaneous OTP requests
    if (hasSentOTP.current) {
      console.log("⏳ OTP request already in progress, skipping...");
      return;
    }

    console.log("📤 Sending OTP request with:", {
      username,
      deviceId: storedDeviceId,
      userIp: storedUserIp
    });

    if (!username) {
      message.error('Username not found');
      console.error("❌ Username is undefined or empty");
      return;
    }

    hasSentOTP.current = true;
    setLoading(true);
    
    try {
      const requestData = {
        username: username.trim()
      };

      console.log("🚀 API Request:", {
        url: "/User/account-reset",
        data: requestData,
        headers: {
          UserDevice: storedDeviceId,
          UserIPAddress: storedUserIp,
        }
      });

      const response = await axiosInstance.post("/User/account-reset", 
        requestData,
        {
          headers: {
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
          },
        }
      );

      console.log("✅ OTP Sent Successfully:", response.data);
      message.success("Verification code sent successfully");
      setTimer(60); // Reset timer
    } catch (error) {
      console.error("❌ OTP Send Error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      const errorMessage = error.response?.data?.message || 
        error.response?.data?.error || 
        "Failed to send verification code. Please try again.";
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
      // Reset the flag after a short delay to prevent rapid consecutive calls
      setTimeout(() => {
        hasSentOTP.current = false;
      }, 2000);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setError("Please enter the verification code");
      return;
    }

    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        username: username,
        otp: otp
      };

      console.log("🔐 Verify OTP Request:", requestData);

      const response = await axiosInstance.post("/User/verify-otp",
        requestData,
        {
          headers: {
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
          },
        }
      );

      console.log("✅ OTP Verified:", response.data);

      if (response.data.code === 200) {
        message.success(response.data.message || "OTP verified successfully");
        const token = response.data.data.jwt;
        localStorage.setItem("verifyToken", token);
        setStep(2); // Move to password change step
        setError("");
      } else {
        setError(response.data.message || "Invalid verification code");
      }
    } catch (error) {
      console.error("❌ OTP Verify Error:", error.response?.data);
      setError(
        error.response?.data?.message || 
        "Failed to verify OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (!newPassword || !confirmPassword) {
      setError("Please fill in both password fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 4) {
      setError("Password must be at least 4 characters");
      return;
    }

    const accessToken = localStorage.getItem("verifyToken");
    
    console.log("🔑 Change Password Request:", {
      password: newPassword,
      confirmPassword: confirmPassword,
      accessToken: accessToken ? "Present" : "Missing",
      deviceId: storedDeviceId,
      userIp: storedUserIp
    });

    setLoading(true);
    try {
      const response = await axiosInstance.post("/User/change-password",
        {
          password: newPassword,
          confirmPassword: confirmPassword
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
          },
        }
      );

      console.log('✅ Password Change Response:', response.data);

      if (response.data.code === 200) {
        message.success(response.data.message || "Password changed successfully!");
        localStorage.removeItem("verifyToken"); // Clean up token
        if (onSuccess) {
          onSuccess(); // Close modal
        }
      } else {
        setError(response.data.message || "Failed to change password");
      }
    } catch (error) {
      console.error("❌ Password Change Error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      const errorMessage = error.response?.data?.message || 
        "Failed to change password. Please try again.";
      
      setError(errorMessage);
      message.error(errorMessage); 
    } finally {
      setLoading(false);
    }
  };

  // Auto-send OTP when modal opens - FIXED VERSION
  useEffect(() => {
    console.log("🎯 Auto-send OTP triggered:", { step, username, isInitialMount: isInitialMount.current });
    
    // Only send OTP on initial mount when step is 1 and username exists
    if (step === 1 && username && isInitialMount.current) {
      isInitialMount.current = false;
      handleSendOTP();
    }
  }, [step, username]);

  // Reset the initial mount flag when modal closes (component unmounts)
  useEffect(() => {
    return () => {
      isInitialMount.current = true;
      hasSentOTP.current = false;
    };
  }, []);

  return (
    <div className="change-password-tab-container">

      {/* STEP 1: OTP VERIFICATION */}
      {step === 1 && (
        <>
          <div className="verification-header">
            <h3 className="verification-title">Verify Your Account</h3>
            <p className="verification-description">
              We've sent a 6-digit verification code to your registered email/phone.
            </p>
          </div>

          <div className="change-password-form-group">
            <label className="change-password-label">Enter Verification Code</label>
            <Input
              maxLength={6}
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value);
                setError("");
              }}
              placeholder="Enter 6-digit code"
              className="profile-settings-input"
            />
            {error && <p className="error-text">{error}</p>}
          </div>

          <Button 
            type="primary" 
            className="change-password-btn" 
            onClick={handleVerifyOTP}
            loading={loading}
            disabled={!otp.trim()}
          >
            Verify & Continue
          </Button>

          <div className="otp-timer">
            {timer > 0 ? (
              <div className="timer-section">
                <span className="timer-text">Didn't receive the code? </span>
                <span className="timer-count">Resend in <b>{timer}s</b></span>
              </div>
            ) : (
              <div className="resend-section">
                <span className="resend-prompt">Still haven't received the code? </span>
                <span className="resend-link" onClick={handleSendOTP}>
                  Resend Verification Code
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* STEP 2: NEW PASSWORD */}
      {step === 2 && (
        <div className="fade-in">
          <div className="verification-header">
            <h3 className="verification-title">Create New Password</h3>
            <p className="verification-description">
              Your identity has been verified. Now create a new password.
            </p>
          </div>

          <div className="change-password-form-group">
            <label className="change-password-label">New Password</label>
            <Input.Password
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError("");
              }}
              placeholder="Create new password"
              className="change-password-input"
            />
          </div>

          <div className="change-password-form-group">
            <label className="change-password-label">Confirm Password</label>
            <Input.Password
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError("");
              }}
              placeholder="Re-enter your new password"
              className="change-password-input"
            />
            {error && <p className="error-text">{error}</p>}
          </div>

          <Button 
            type="primary" 
            className="change-password-btn" 
            onClick={handleChangePassword}
            loading={loading}
            disabled={!newPassword || !confirmPassword}
          >
            Update Password
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChangePasswordTab;